
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Upload } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Progress } from "@/components/ui/progress";
import { Loader } from "@/components/ui/loader";

export function BulkUploader() {
  const [files, setFiles] = useState<FileList | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const { toast } = useToast();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFiles(e.target.files);
      console.log("Files selected:", e.target.files.length);
    }
  };

  const handleUpload = async () => {
    if (!files || files.length === 0) {
      toast({
        title: "No files selected",
        description: "Please select files to upload",
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);
    setProgress(0);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      console.log("Current user:", user?.id);
      
      if (!user) {
        throw new Error("User not authenticated");
      }

      const totalFiles = files.length;
      let uploadedFiles = 0;

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        console.log(`Processing file ${i + 1}/${totalFiles}: ${file.name}`);
        
        // Upload file directly without processing
        const sanitizedFileName = file.name.replace(/[^\x00-\x7F]/g, '');
        const fileExt = sanitizedFileName.split('.').pop();
        const filePath = `${user.id}/${crypto.randomUUID()}.${fileExt}`;

        console.log(`Uploading file ${i + 1}/${totalFiles} to storage with path:`, filePath);

        const { error: uploadError } = await supabase.storage
          .from('templates')
          .upload(filePath, file, {
            contentType: file.type,
            upsert: false
          });

        if (uploadError) {
          console.error('Upload error:', uploadError);
          throw uploadError;
        }

        console.log(`Saving template ${i + 1}/${totalFiles} to database with user_id:`, user.id);

        const { error: dbError } = await supabase
          .from('templates')
          .insert({
            filename: sanitizedFileName,
            file_path: filePath,
            content_type: file.type,
            size: file.size,
            user_id: user.id,
            needs_review: true,
            is_approved: false
          });

        if (dbError) {
          console.error('Database error:', dbError);
          // Cleanup the uploaded file if database insert fails
          await supabase.storage
            .from('templates')
            .remove([filePath]);
          throw dbError;
        }

        uploadedFiles++;
        setProgress((uploadedFiles / totalFiles) * 100);
      }

      toast({
        title: "Success",
        description: `Successfully uploaded ${totalFiles} templates for review`,
      });

      setFiles(null);
      if (document.getElementById('file-upload') instanceof HTMLInputElement) {
        (document.getElementById('file-upload') as HTMLInputElement).value = '';
      }

    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: "Upload failed",
        description: error instanceof Error ? error.message : "Failed to upload templates",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
      setProgress(0);
    }
  };

  return (
    <div className="space-y-4">
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
        <Upload className="mx-auto h-12 w-12 text-gray-400" />
        <div className="mt-4">
          <Input
            type="file"
            onChange={handleFileChange}
            className="hidden"
            id="file-upload"
            multiple
            accept=".txt,.doc,.docx,.pdf"
          />
          <label
            htmlFor="file-upload"
            className="cursor-pointer text-blue-500 hover:text-blue-600"
          >
            Choose files
          </label>
          <p className="mt-2 text-sm text-gray-500">
            {files ? `${files.length} files selected` : "Select multiple files"}
          </p>
        </div>
      </div>

      {isUploading && (
        <div className="space-y-4">
          <div className="flex justify-center pb-2">
            <Loader text={`Uploading... ${Math.round(progress)}%`} />
          </div>
          <Progress value={progress} className="w-full" />
        </div>
      )}

      <Button
        onClick={handleUpload}
        className="w-full"
        disabled={!files || isUploading}
      >
        {isUploading ? "Uploading..." : "Upload Templates"}
      </Button>
    </div>
  );
}
